import React, { useMemo, useState } from 'react';
import { Alert, Badge, Card, Col, Form, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { formatCurrency } from '../../utils/formatters';
import { formatDate } from '../../utils/dateFormatters';

// ---------------------------------------------------------------------------
// Shape of the results JSON saved by GLBankReconciliation Lambda
// ---------------------------------------------------------------------------

interface GLEntry {
  entry_id?: string;
  entry_date: string;
  account_name?: string;
  debit_amount: number;
  credit_amount: number;
  name?: string;
  memo?: string;
  reference_number?: string;
  transaction_type?: string;
  note?: string;
}

interface BankTxn {
  _txn_id?: string;
  date: string;
  description: string;
  amount: number;
  type?: string;
  bank_name?: string;
  account_number?: string;
  statement_id?: string;
  file_id?: string;
  note?: string;
}

interface MatchedPair {
  gl_entry: GLEntry;
  bank_transaction: BankTxn;
  amount: number;
  date_delta_days?: number;
}

interface AlteredCheck {
  gl_entry: GLEntry;
  bank_transaction: BankTxn;
  gl_amount: number;
  bank_amount: number;
  check_number: string;
  note?: string;
}

interface Summary {
  matched_count: number;
  matched_total: number;
  gl_only_count: number;
  gl_only_total: number;
  bank_only_count: number;
  bank_only_total: number;
  altered_count: number;
}

export interface GLBankReconciliationResults {
  gl_account_name: string;
  bank_name: string;
  account_number: string;
  date_range: { from: string; to: string };
  gl_entries_count: number;
  bank_txns_count: number;
  summary: Summary;
  matched: MatchedPair[];
  gl_only: GLEntry[];
  bank_only: BankTxn[];
  altered: AlteredCheck[];
}

interface GLBankReconciliationResultsProps {
  results: GLBankReconciliationResults;
  analysisName?: string;
  caseNumber?: string;
  caseTitle?: string;
  caseId?: string;
}

// ---------------------------------------------------------------------------
// Timeline row types
// ---------------------------------------------------------------------------

type TimelineRow =
  | { kind: 'matched';   pair: MatchedPair;   sortDate: string }
  | { kind: 'gl_only';   gl: GLEntry;          sortDate: string }
  | { kind: 'bank_only'; bank: BankTxn;        sortDate: string }
  | { kind: 'altered';   item: AlteredCheck;   sortDate: string };

type FilterKind = 'all' | 'matched' | 'gl_only' | 'bank_only' | 'altered';

// ---------------------------------------------------------------------------
// Cell helpers
// ---------------------------------------------------------------------------

function glEntryAmount(gl: GLEntry): number {
  return (gl.debit_amount || 0) + (gl.credit_amount || 0);
}

const GLCell: React.FC<{ gl: GLEntry }> = ({ gl }) => {
  const label = gl.name || gl.memo || '';
  const parts = [formatCurrency(glEntryAmount(gl))];
  if (label) parts.push(label);
  if (gl.reference_number) parts.push(`#${gl.reference_number}`);
  if (gl.transaction_type) parts.push(gl.transaction_type);
  return (
    <div
      style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      title={parts.slice(1).join(' · ')}
    >
      <span className="fw-semibold">{parts[0]}</span>
      {parts.slice(1).map((p, i) => (
        <span key={i} className="text-muted" style={{ fontSize: '0.8rem' }}>{' · '}{p}</span>
      ))}
    </div>
  );
};

const BankCell: React.FC<{ txn: BankTxn; caseId?: string }> = ({ txn, caseId }) => {
  const parts: string[] = [formatCurrency(txn.amount)];
  if (txn.description) parts.push(txn.description);
  if (txn.type) parts.push(txn.type);
  return (
    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={parts.slice(1).join(' · ')}>
      <span className="fw-semibold">{parts[0]}</span>
      {parts.slice(1).map((p, i) => (
        <span key={i} className="text-muted" style={{ fontSize: '0.8rem' }}>{' · '}{p}</span>
      ))}
      {caseId && txn.file_id && (
        <>{' '}<Link to={`/case/${caseId}/file/${txn.file_id}`} className="text-decoration-none" style={{ fontSize: '0.75rem' }}>📄</Link></>
      )}
    </div>
  );
};

const StatusBadge: React.FC<{ kind: TimelineRow['kind']; dateDelta?: number }> = ({ kind, dateDelta }) => {
  if (kind === 'matched')   return <div className="text-center" style={{ color: '#198754' }}>✓{dateDelta != null && dateDelta > 0 ? <span className="text-muted" style={{ fontSize: '0.65rem', display: 'block' }}>Δ{dateDelta}d</span> : null}</div>;
  if (kind === 'gl_only')   return <div className="text-center text-danger fw-bold" style={{ fontSize: '0.7rem' }}>GL only</div>;
  if (kind === 'bank_only') return <div className="text-center text-warning fw-bold" style={{ fontSize: '0.7rem' }}>Bank only</div>;
  if (kind === 'altered')   return <div className="text-center text-danger fw-bold" style={{ fontSize: '0.7rem' }}>⚠ Alt.</div>;
  return null;
};

// ---------------------------------------------------------------------------
// Summary cards
// ---------------------------------------------------------------------------

const SummaryCards: React.FC<{
  summary: Summary;
  gl_entries_count: number;
  bank_txns_count: number;
}> = ({ summary, gl_entries_count, bank_txns_count }) => (
  <Row className="g-3 mb-4">
    <Col xs={6} md={3}>
      <Card border="success" className="h-100 text-center">
        <Card.Body className="py-2">
          <div className="fs-3 fw-bold text-success">{summary.matched_count}</div>
          <div className="text-muted small">Matched</div>
          <div className="text-muted small">{formatCurrency(summary.matched_total)}</div>
          <div className="text-muted" style={{ fontSize: '0.65rem' }}>
            {gl_entries_count} GL / {bank_txns_count} bank loaded
          </div>
        </Card.Body>
      </Card>
    </Col>
    <Col xs={6} md={3}>
      <Card border="danger" className="h-100 text-center">
        <Card.Body className="py-2">
          <div className="fs-3 fw-bold text-danger">{summary.gl_only_count}</div>
          <div className="text-muted small">GL Only</div>
          <div className="text-muted small">{formatCurrency(summary.gl_only_total)}</div>
          <div className="text-muted" style={{ fontSize: '0.65rem' }}>in books, not bank</div>
        </Card.Body>
      </Card>
    </Col>
    <Col xs={6} md={3}>
      <Card border="warning" className="h-100 text-center">
        <Card.Body className="py-2">
          <div className="fs-3 fw-bold text-warning">{summary.bank_only_count}</div>
          <div className="text-muted small">Bank Only</div>
          <div className="text-muted small">{formatCurrency(summary.bank_only_total)}</div>
          <div className="text-muted" style={{ fontSize: '0.65rem' }}>in bank, not books</div>
        </Card.Body>
      </Card>
    </Col>
    <Col xs={6} md={3}>
      <Card
        border={summary.altered_count > 0 ? 'danger' : 'secondary'}
        className="h-100 text-center"
      >
        <Card.Body className="py-2">
          <div className={`fs-3 fw-bold ${summary.altered_count > 0 ? 'text-danger' : 'text-secondary'}`}>
            {summary.altered_count}
          </div>
          <div className="text-muted small">Altered Checks</div>
          <div className="text-muted" style={{ fontSize: '0.65rem' }}>
            {summary.altered_count > 0 ? '⚠ Review required' : 'None detected'}
          </div>
        </Card.Body>
      </Card>
    </Col>
  </Row>
);

// ---------------------------------------------------------------------------
// Row styles
// ---------------------------------------------------------------------------

function rowStyle(kind: TimelineRow['kind']): React.CSSProperties {
  if (kind === 'matched')   return { background: '#f6fff8' };
  if (kind === 'gl_only')   return { background: '#fff5f5' };
  if (kind === 'bank_only') return { background: '#fffbf0' };
  if (kind === 'altered')   return { background: '#fff0f0', borderLeft: '3px solid #dc3545' };
  return {};
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const GLBankReconciliationResultsComponent: React.FC<GLBankReconciliationResultsProps> = ({
  results,
  analysisName,
  caseNumber,
  caseTitle,
  caseId,
}) => {
  const [filter, setFilter] = useState<FilterKind>('all');
  const { summary, matched, gl_only, bank_only, altered } = results;

  // Build and sort chronological timeline
  const timeline = useMemo<TimelineRow[]>(() => {
    const rows: TimelineRow[] = [
      ...altered.map(item  => ({ kind: 'altered'   as const, item,  sortDate: item.gl_entry.entry_date || '' })),
      ...matched.map(pair  => ({ kind: 'matched'   as const, pair,  sortDate: pair.gl_entry.entry_date || pair.bank_transaction.date || '' })),
      ...gl_only.map(gl    => ({ kind: 'gl_only'   as const, gl,    sortDate: gl.entry_date || '' })),
      ...bank_only.map(bank => ({ kind: 'bank_only' as const, bank, sortDate: bank.date || '' })),
    ];
    return rows.sort((a, b) => a.sortDate.localeCompare(b.sortDate));
  }, [matched, gl_only, bank_only, altered]);

  const filtered = useMemo(
    () => filter === 'all' ? timeline : timeline.filter(r => r.kind === filter),
    [timeline, filter]
  );

  const hasSuspicious =
    summary.gl_only_count > 0 || summary.bank_only_count > 0 || summary.altered_count > 0;

  const filterOptions: [FilterKind, string][] = [
    ['all',       `All (${timeline.length})`],
    ['matched',   `✓ Matched (${summary.matched_count})`],
    ['gl_only',   `GL Only (${summary.gl_only_count})`],
    ['bank_only', `Bank Only (${summary.bank_only_count})`],
    ['altered',   `⚠ Altered (${summary.altered_count})`],
  ];

  return (
    <div>
      {/* ── Header ── */}
      <div className="mb-3">
        {(caseNumber || caseTitle) && (
          <div className="text-muted small mb-1">{caseNumber} {caseTitle}</div>
        )}
        <h5 className="mb-0">{analysisName || 'GL ↔ Bank Reconciliation'}</h5>
        <div className="text-muted small">
          {results.gl_account_name} ↔ {results.bank_name}
          {results.account_number ? ` (…${results.account_number})` : ''}
          {' · '}
          {formatDate(results.date_range.from)} – {formatDate(results.date_range.to)}
        </div>
      </div>

      {/* ── Altered check alert ── */}
      {altered.length > 0 && (
        <Alert variant="danger" className="mb-3 py-2">
          <strong>
            ⚠ {summary.altered_count} possible altered check{summary.altered_count > 1 ? 's' : ''} detected
          </strong>
          {' — '}same check number, different amount between GL and bank.
        </Alert>
      )}

      {/* ── All-clear banner ── */}
      {!hasSuspicious && (
        <Alert variant="success" className="mb-3 py-2">
          All {summary.matched_count} transactions matched. No discrepancies found.
        </Alert>
      )}

      {/* ── Summary cards ── */}
      <SummaryCards
        summary={summary}
        gl_entries_count={results.gl_entries_count}
        bank_txns_count={results.bank_txns_count}
      />

      {/* ── Filter bar ── */}
      <div className="d-flex align-items-center gap-2 mb-3 flex-wrap">
        <span className="text-muted small me-1">Show:</span>
        {filterOptions.map(([val, label]) => (
          <Form.Check
            key={val}
            inline
            type="radio"
            label={label}
            name="recon-filter"
            id={`recon-filter-${val}`}
            checked={filter === val}
            onChange={() => setFilter(val)}
          />
        ))}
      </div>

      {/* ── Side-by-side timeline table ── */}
      <div style={{ overflowX: 'auto' }}>
        <table
          className="table table-sm table-bordered mb-0"
          style={{ minWidth: 700, fontSize: '0.85rem', tableLayout: 'fixed', width: '100%' }}
        >
          <thead className="table-light">
            <tr>
              <th style={{ width: 80 }}>GL Date</th>
              <th>General Ledger Entry</th>
              <th style={{ width: 55, textAlign: 'center' }}>Match</th>
              <th style={{ width: 80 }}>Bank Date</th>
              <th>Bank Transaction</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center text-muted py-3">
                  No entries for this filter.
                </td>
              </tr>
            )}
            {filtered.map((row, i) => {
              if (row.kind === 'matched') {
                const { pair } = row;
                return (
                  <tr key={i} style={rowStyle('matched')}>
                    <td className="text-muted">{formatDate(pair.gl_entry.entry_date)}</td>
                    <td><GLCell gl={pair.gl_entry} /></td>
                    <td><StatusBadge kind="matched" dateDelta={pair.date_delta_days} /></td>
                    <td className="text-muted">{formatDate(pair.bank_transaction.date)}</td>
                    <td><BankCell txn={pair.bank_transaction} caseId={caseId} /></td>
                  </tr>
                );
              }
              if (row.kind === 'gl_only') {
                return (
                  <tr key={i} style={rowStyle('gl_only')}>
                    <td className="text-muted">{formatDate(row.gl.entry_date)}</td>
                    <td><GLCell gl={row.gl} /></td>
                    <td><StatusBadge kind="gl_only" /></td>
                    <td className="text-muted text-center">—</td>
                    <td className="text-muted fst-italic" style={{ fontSize: '0.8rem' }}>No matching bank txn</td>
                  </tr>
                );
              }
              if (row.kind === 'bank_only') {
                return (
                  <tr key={i} style={rowStyle('bank_only')}>
                    <td className="text-muted text-center">—</td>
                    <td className="text-muted fst-italic" style={{ fontSize: '0.8rem' }}>No matching GL entry</td>
                    <td><StatusBadge kind="bank_only" /></td>
                    <td className="text-muted">{formatDate(row.bank.date)}</td>
                    <td><BankCell txn={row.bank} caseId={caseId} /></td>
                  </tr>
                );
              }
              if (row.kind === 'altered') {
                const { item } = row;
                return (
                  <tr key={i} style={rowStyle('altered')}>
                    <td className="text-muted">{formatDate(item.gl_entry.entry_date)}</td>
                    <td><GLCell gl={item.gl_entry} /><span className="text-danger" style={{ fontSize: '0.75rem' }}>{' '}(GL {formatCurrency(item.gl_amount)})</span></td>
                    <td><StatusBadge kind="altered" /></td>
                    <td className="text-muted">{formatDate(item.bank_transaction.date)}</td>
                    <td><BankCell txn={item.bank_transaction} caseId={caseId} /><span className="text-danger" style={{ fontSize: '0.75rem' }}>{' '}(Bank {formatCurrency(item.bank_amount)})</span></td>
                  </tr>
                );
              }
              return null;
            })}
          </tbody>
        </table>
      </div>

      {filtered.length > 100 && (
        <div className="text-muted small text-center mt-2">
          Showing {filtered.length} rows — use the filter buttons above to narrow down.
        </div>
      )}
    </div>
  );
};

export default GLBankReconciliationResultsComponent;
